// Migration off the retired `pub.mini.*` record NSIDs (szzt-spec.md §4).
//
// The site and deployment collections were renamed on the wire — `pub.mini.site` →
// `app.szzt.site`, `pub.mini.deployment` → `app.szzt.deployment` — with no migration path, so every
// site published before the rename is stranded: its records still live in the retired collections
// and the read path (`@szzt/records` `COLLECTION`, `resolveTarget`) no longer looks there.
//
// `ing.dasl.masl` is an upstream DASL Web Tiles record adopted as-is. It did NOT move. It is never
// rewritten, never re-created, and never deleted by anything in this file — a deployment's `tile`
// strong ref keeps pointing at the same manifest record under the same CID, so it copies verbatim.
//
// `$type` is the ONLY field whose value differs between a legacy record and its migrated
// counterpart. `root`, `entry`, `createdAt`, `title`/`slug`/`hints` and any field this code does not
// know about all copy through unchanged. Two additions, both forced by the rename:
//   - a legacy deployment lacks the optional `site` back-reference (§4.2) — which is exactly what
//     makes it unattributable and invisible to a site's version history — so the migration fills it
//     in from the owning site's rkey.
//   - the site's `current` strong ref must be re-pinned in BOTH halves: the rkey is preserved but
//     the collection changes (new `uri`), and rewriting `$type` changes the deployment record's
//     atproto CID (new `cid`). The new CID is precomputed client-side from the new record value, the
//     same mechanism `publish` uses to pin `tile.cid`/`current.cid` inside one atomic write (§7.2);
//     the executor still reads the stored record back and re-pins from it if the PDS disagreed.
//
// ADDITIVE-FIRST, deliberately. `migrate()` only CREATES records under the new NSIDs, preserving each
// record's ORIGINAL rkey, and never deletes a legacy original — so a failed or partial migration
// leaves the pre-rename repo exactly as it was. Deleting the legacy originals is a separate second
// pass the caller has to ask for by name (`planLegacyDeletion` + `deleteLegacyRecords`), and that
// pass refuses to delete any legacy record whose migrated counterpart is not already in the repo.
//
// The WHOLE deployment history migrates, not just the version `current` names. A legacy deployment
// left behind is invisible to the version-history panel, cannot be reverted onto, and can never be
// pruned — so its manifest record and its blobs are pinned out of PDS garbage collection forever.
//
// Split like the rest of the write path and mirroring `planPrune`/`prune`: a PURE planner
// (`planMigration`) over the supplied legacy records that writes nothing and returns a serializable
// plan, and a THIN executor (`migrate`) over the `PdsClient` seam. Enumerating the repo is the
// caller's job at the same seam boundary the other planners keep — `listLegacyRepo` is offered here
// for it, over its own narrow read interface rather than by widening the write seam.
//
// The retired NSIDs live in THIS file and nowhere else. `@szzt/records` stays ignorant of them on
// purpose, so no read path can grow a silent fallback into a collection that is supposed to be gone.

import { parseAtUri, readLink, readStrongRef, ResolveError } from "@szzt/records";
import { atUri, COLLECTION, recordCid } from "./records.ts";
import { rollback } from "./rollback.ts";
import {
  type ApplyWrite,
  type CommitRef,
  type DeploymentRecord,
  type FetchedRecord,
  type PdsClient,
  type RepoRecordLister,
  type SiteRecord,
  type StrongRef,
  SwapFailedError,
} from "./types.ts";

/**
 * The retired NSIDs, kept out of `@szzt/records` on purpose (see this file's header). Only the two
 * collections that actually moved are listed: `ing.dasl.masl` is upstream, was adopted as-is, and
 * has no legacy name — there is deliberately no `masl` entry to migrate to or from.
 */
export const LEGACY_COLLECTION = {
  site: "pub.mini.site",
  deployment: "pub.mini.deployment",
} as const;

/** A legacy record the planner declined to migrate (or the deletion planner declined to delete). */
export interface SkippedRecord {
  uri: string;
  reason: string;
}

/** Thrown when a legacy record's shape makes the migration unplannable. The planner writes nothing. */
export class MigrationShapeError extends Error {
  constructor(
    readonly uri: string,
    reason: string,
  ) {
    super(`cannot plan a migration for ${uri}: ${reason}`);
    this.name = "MigrationShapeError";
  }
}

/** Thrown when execution finds an `app.szzt.site` record already at the plan's rkey (§4.1). */
export class AlreadyMigratedError extends Error {
  constructor(readonly siteUri: string) {
    super(
      `refusing to migrate onto an existing record: ${siteUri} already exists — re-running a ` +
        `completed migration would overwrite the live site record and re-pin \`current\` at a stale ` +
        `deployment CID`,
    );
    this.name = "AlreadyMigratedError";
  }
}

/** Thrown by the deletion pass when a legacy record's migrated counterpart is not in the repo. */
export class UnmigratedLegacyRecordError extends Error {
  constructor(
    readonly legacyUri: string,
    readonly expectedUri: string,
  ) {
    super(
      `refusing to delete legacy record ${legacyUri}: its migrated counterpart ${expectedUri} does ` +
        `not exist — deleting it would destroy the only copy`,
    );
    this.name = "UnmigratedLegacyRecordError";
  }
}

/** Read a record value as a plain object, or throw {@link MigrationShapeError}. */
function asRecordValue(value: unknown, uri: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new MigrationShapeError(uri, "record value is not an object");
  }
  return { ...(value as Record<string, unknown>) };
}

/** Human-readable reason from a thrown shape/resolve error. */
function reasonOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

// ---------------------------------------------------------------------------------------------
// Enumeration (read-only)
// ---------------------------------------------------------------------------------------------

/** Every legacy record in one repo, as {@link listLegacyRepo} found it. */
export interface LegacyRepo {
  /** All `pub.mini.site` records. */
  sites: FetchedRecord[];
  /** All `pub.mini.deployment` records — the complete history, across every site. */
  deployments: FetchedRecord[];
}

/**
 * Enumerate every retired-collection record in a repo, following `listRecords` cursors to the end
 * so a site with more than one page of history is not silently truncated. Read-only: it writes
 * nothing and needs no more authority than a public repo listing.
 *
 * Paginating to exhaustion is the load-bearing part. A migration that stops at the first page
 * strands the deployments it never saw, and a stranded deployment can never be pruned — its
 * manifest and blobs are pinned out of PDS garbage collection permanently.
 */
export async function listLegacyRepo(
  lister: RepoRecordLister,
  pageSize = 100,
): Promise<LegacyRepo> {
  return {
    sites: await listAll(lister, LEGACY_COLLECTION.site, pageSize),
    deployments: await listAll(lister, LEGACY_COLLECTION.deployment, pageSize),
  };
}

/** Drain every page of one collection. Stops on a cursor that does not advance, so a PDS that keeps
 *  echoing the same cursor cannot spin this forever. */
async function listAll(
  lister: RepoRecordLister,
  collection: string,
  pageSize: number,
): Promise<FetchedRecord[]> {
  const out: FetchedRecord[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    const page = await lister.listRecords({
      collection,
      limit: pageSize,
      ...(cursor !== undefined ? { cursor } : {}),
    });
    out.push(...page.records);
    const next = page.cursor;
    if (next === undefined || next === cursor || seenCursors.has(next)) return out;
    seenCursors.add(next);
    cursor = next;
  }
}

/** Which legacy deployments a given legacy site can claim — see {@link legacyDeploymentsForSite}. */
export interface LegacyAttribution {
  /** Deployments this site owns: migrate all of these together. */
  deployments: FetchedRecord[];
  /** Deployments no site can claim: more than one legacy site exists, and the deployment carries no
   *  usable `site` back-reference — either none at all, or one naming an rkey absent from
   *  `repo.sites`. Report these; migrating them would attribute them by guess. With
   *  {@link LegacyAttributionOptions.attributeUnclaimedToNearestSite} set this holds only what
   *  remains immeasurable: a deployment or every site carrying an rkey that is not a decodable TID,
   *  which has no distance to compare and so is still refused rather than guessed. */
  ambiguous: FetchedRecord[];
}

/** Options for {@link legacyDeploymentsForSite}. */
export interface LegacyAttributionOptions {
  /**
   * HEURISTIC, off by default. A legacy deployment carries no `site` back-reference, so in a repo
   * with more than one legacy site only the deployment each site's `current` names is decidable —
   * everything else is refused as `ambiguous` by default, and a refused deployment stays under the
   * retired NSIDs forever: invisible to the version-history panel, unreachable by `prune`, its
   * manifest and blobs pinned out of PDS garbage collection for good. Setting this true attributes
   * each otherwise-`ambiguous` deployment to the legacy site whose rkey is NEAREST to it by absolute
   * TID distance instead. The cost of a wrong guess is a version filed under the wrong site's
   * history — visible and correctable; the cost of refusing is a permanently unprunable record. Off
   * by default so the library never guesses on its own; a caller opts in only once it has decided
   * that trade is worth making for its repo.
   */
  attributeUnclaimedToNearestSite?: boolean;
}

/**
 * Attribute a repo's legacy deployments to one legacy site. A legacy deployment carries no `site`
 * back-reference (that field is what the migration adds), so by default attribution is decidable in
 * exactly three cases:
 *   - the deployment the site's `current` names is unambiguously that site's;
 *   - a deployment that already carries a `site` back-reference naming a site actually present in
 *     `repo.sites` is that site's — a back-reference naming an rkey absent from the repo claims
 *     nothing and falls through to the rules below rather than being silently dropped;
 *   - when the repo holds exactly ONE legacy site, every remaining deployment is that site's,
 *     because nothing else in the repo could own it.
 *
 * Everything else is reported as `ambiguous` rather than guessed — a wrong attribution writes a
 * `site` back-reference that files someone's version under the wrong site's history. Passing
 * {@link LegacyAttributionOptions.attributeUnclaimedToNearestSite} replaces that refusal with a
 * fourth, heuristic rule; see its doc comment for what it trades away and why it exists.
 */
export function legacyDeploymentsForSite(
  repo: LegacyRepo,
  siteUri: string,
  options?: LegacyAttributionOptions,
): LegacyAttribution {
  const siteRkey = parseAtUri(siteUri).rkey;
  const nearest = options?.attributeUnclaimedToNearestSite === true;

  // rkeys any legacy site names as its live version — each belongs to that site and no other.
  const currentByRkey = new Map<string, string>();
  const siteRkeys = new Set<string>();
  const siteTids: { rkey: string; tid: bigint }[] = [];
  for (const site of repo.sites) {
    const rkey = parseAtUri(site.uri).rkey;
    siteRkeys.add(rkey);
    if (nearest) {
      const tid = decodeTid(rkey);
      if (tid !== undefined) siteTids.push({ rkey, tid });
    }
    const uri = currentUriOf(site.value);
    if (uri === undefined) continue;
    try {
      currentByRkey.set(parseAtUri(uri).rkey, rkey);
    } catch {
      // A malformed `current` claims nothing; the planner reports it when it plans this site.
    }
  }

  const deployments: FetchedRecord[] = [];
  const ambiguous: FetchedRecord[] = [];
  for (const dep of repo.deployments) {
    let depRkey: string;
    try {
      depRkey = parseAtUri(dep.uri).rkey;
    } catch {
      continue; // not a record AT-URI; the planner reports it as skipped if it is handed one
    }
    // A back-reference is only load-bearing when it names one of the legacy sites in `repo.sites`.
    // Migration preserves rkeys, so a reference to an already-migrated site still matches its legacy
    // counterpart's rkey; one that matches nothing here claims nothing, and falls through exactly
    // like a deployment with no back-reference at all rather than vanishing silently.
    const backRef = backReferencedSite(dep.value);
    const claimed =
      (backRef !== undefined && siteRkeys.has(backRef) ? backRef : undefined) ??
      currentByRkey.get(depRkey);
    if (claimed !== undefined) {
      if (claimed === siteRkey) deployments.push(dep);
      continue;
    }
    if (repo.sites.length === 1) {
      deployments.push(dep);
      continue;
    }
    if (nearest) {
      const depTid = decodeTid(depRkey);
      const nearestSite = depTid === undefined ? undefined : nearestSiteRkey(depTid, siteTids);
      if (nearestSite !== undefined) {
        if (nearestSite === siteRkey) deployments.push(dep);
        continue;
      }
    }
    ambiguous.push(dep);
  }
  return { deployments, ambiguous };
}

/**
 * The sortable base32 alphabet atproto TIDs are written over: `234567abcdefghijklmnopqrstuvwxyz`,
 * digits before letters, chosen so plain ASCII/string ordering matches chronological ordering. That
 * is exactly why a string compare between two rkeys is not enough here: it correctly says which one
 * came first, but not how FAR apart they are, and nearest-site attribution needs the distance.
 */
const TID_ALPHABET = "234567abcdefghijklmnopqrstuvwxyz";

/**
 * Decode a 13-character atproto TID rkey into the microsecond timestamp it encodes, most significant
 * character first. Returns `undefined` when the rkey is not a well-formed TID — wrong length or a
 * character outside {@link TID_ALPHABET} — because such an rkey cannot be measured, only guessed at.
 */
function decodeTid(rkey: string): bigint | undefined {
  if (rkey.length !== 13) return undefined;
  let value = 0n;
  for (const ch of rkey) {
    const digit = TID_ALPHABET.indexOf(ch);
    if (digit === -1) return undefined;
    value = value * 32n + BigInt(digit);
  }
  return value;
}

/**
 * The legacy site nearest a deployment's TID by absolute distance — not "greatest site rkey at or
 * before the deployment", because `publish` writes a site's first deployment and the site record in
 * ONE atomic commit with the deployment FIRST, so a site's own first deployment has a TID a few
 * microseconds SMALLER than its site record. A preceding-only rule would misfile every site's first
 * deployment onto whichever site came before it. Ties (equal distance to two sites) resolve to the
 * lexically smaller site rkey, deterministically. `undefined` only when `sites` is empty.
 */
function nearestSiteRkey(
  depTid: bigint,
  sites: readonly { rkey: string; tid: bigint }[],
): string | undefined {
  let best: { rkey: string; tid: bigint } | undefined;
  let bestDist: bigint | undefined;
  for (const site of sites) {
    const dist = site.tid > depTid ? site.tid - depTid : depTid - site.tid;
    if (bestDist === undefined || dist < bestDist || (dist === bestDist && site.rkey < best!.rkey)) {
      best = site;
      bestDist = dist;
    }
  }
  return best?.rkey;
}

/** A site record value's `current.uri`, or `undefined` when absent/malformed. */
function currentUriOf(value: unknown): string | undefined {
  try {
    return readStrongRef(value, "current", LEGACY_COLLECTION.site).uri;
  } catch {
    return undefined;
  }
}

/** A deployment record value's `site` back-reference (an rkey), or `undefined` when absent. */
function backReferencedSite(value: unknown): string | undefined {
  if (typeof value !== "object" || value === null) return undefined;
  const site = (value as { site?: unknown }).site;
  return typeof site === "string" ? site : undefined;
}

// ---------------------------------------------------------------------------------------------
// Planner (pure, read-only)
// ---------------------------------------------------------------------------------------------

/** Input to the pure {@link planMigration}. */
export interface MigrationPlanInput {
  /** The legacy `pub.mini.site` record: its AT-URI and raw IPLD-JSON value. */
  site: FetchedRecord;
  /**
   * EVERY legacy `pub.mini.deployment` record belonging to that site — the whole history, not just
   * the version `current` names. Enumerating the repo is the caller's job (the same seam boundary
   * `planPrune` keeps); {@link listLegacyRepo} + {@link legacyDeploymentsForSite} produce this.
   */
  deployments: readonly FetchedRecord[];
}

/** One legacy deployment as it will be re-created under `app.szzt.deployment`. */
export interface MigratedDeployment {
  /** AT-URI of the legacy `pub.mini.deployment` record. Left in place by {@link migrate}. */
  legacyUri: string;
  /** AT-URI of the new `app.szzt.deployment` record — same rkey, new collection. */
  uri: string;
  /** Preserved rkey, identical to the legacy record's. */
  rkey: string;
  /** atproto CID of {@link value}, precomputed client-side so the site's `current` can pin it. */
  cid: string;
  /** The new record value: the legacy value with `$type` rewritten (and `site` filled if absent). */
  value: DeploymentRecord;
  /** True when the legacy record carried no `site` back-reference and the migration added one. */
  backfilledSite: boolean;
}

/** The computed migration: what gets created, under which rkeys, and what was skipped. */
export interface MigrationPlan {
  /** Repo DID every record in this plan belongs to. */
  did: string;
  /** Preserved rkey of the site record, shared by the legacy and migrated copies. */
  siteRkey: string;
  /** AT-URI of the legacy `pub.mini.site` record (retained — nothing here deletes it). */
  legacySiteUri: string;
  /** AT-URI of the new `app.szzt.site` record. */
  siteUri: string;
  /** The new site record value: the legacy value with `$type` rewritten and `current` re-pinned. */
  siteValue: SiteRecord;
  /** atproto CID of {@link siteValue}, precomputed client-side. */
  siteCid: string;
  /** The re-pinned active-version strong ref: new collection in `uri`, new record CID in `cid`. */
  current: StrongRef;
  /** The whole migrated history, ordered by rkey ascending (TID rkeys sort chronologically). */
  deployments: MigratedDeployment[];
  /**
   * The creates for one atomic `applyWrites`: every deployment FIRST, then the site record. The
   * order is explicit and load-bearing — the site's `current` pins a deployment record CID, so the
   * deployment it names must be part of the same commit and is listed before it.
   *
   * Every write here is a `create`. There is no `delete` in a migration plan, by construction: the
   * legacy originals are only removed by the separate {@link planLegacyDeletion} pass.
   */
  writes: ApplyWrite[];
  /** Legacy deployments the planner would not migrate, each with a reason. */
  skipped: SkippedRecord[];
}

/**
 * Plan the additive migration of one legacy site and its entire deployment history onto the current
 * NSIDs. PURE and read-only: it takes no client, performs no I/O beyond hashing the record values it
 * builds, and returns a serializable plan the caller can inspect before {@link migrate} executes it.
 *
 * Async only because pinning `current` needs the new deployment record's atproto CID, which is the
 * dCBOR42 hash of the value — precomputed client-side exactly as `publish` does (§7.2).
 *
 * Throws {@link MigrationShapeError}, with no plan produced, when the site record itself is
 * unusable or when the deployment its `current` names is missing from `deployments` — a plan that
 * cannot re-pin `current` would migrate a site into a dangling pointer. Individual malformed
 * deployments are reported in {@link MigrationPlan.skipped} instead, so one bad record cannot block
 * an otherwise-migratable history.
 */
export async function planMigration(input: MigrationPlanInput): Promise<MigrationPlan> {
  const legacySite = input.site;
  const siteParts = parseAtUri(legacySite.uri);
  if (siteParts.collection !== LEGACY_COLLECTION.site) {
    throw new MigrationShapeError(
      legacySite.uri,
      `not a ${LEGACY_COLLECTION.site} record (collection is ${siteParts.collection})`,
    );
  }
  const { did, rkey: siteRkey } = siteParts;
  const legacySiteValue = asRecordValue(legacySite.value, legacySite.uri);

  let legacyCurrent: StrongRef;
  try {
    legacyCurrent = readStrongRef(legacySite.value, "current", LEGACY_COLLECTION.site);
  } catch (err) {
    throw new MigrationShapeError(legacySite.uri, reasonOf(err));
  }
  let currentRkey: string;
  try {
    const cur = parseAtUri(legacyCurrent.uri);
    if (cur.collection !== LEGACY_COLLECTION.deployment) {
      throw new ResolveError(
        `current names ${cur.collection}, not ${LEGACY_COLLECTION.deployment} — already migrated?`,
      );
    }
    currentRkey = cur.rkey;
  } catch (err) {
    throw new MigrationShapeError(legacySite.uri, reasonOf(err));
  }

  const deployments: MigratedDeployment[] = [];
  const skipped: SkippedRecord[] = [];
  const seen = new Set<string>();

  for (const record of input.deployments) {
    let parts;
    try {
      parts = parseAtUri(record.uri);
    } catch (err) {
      skipped.push({ uri: record.uri, reason: reasonOf(err) });
      continue;
    }
    if (parts.collection !== LEGACY_COLLECTION.deployment) {
      skipped.push({
        uri: record.uri,
        reason: `not a ${LEGACY_COLLECTION.deployment} record (collection is ${parts.collection})`,
      });
      continue;
    }
    if (parts.did !== did) {
      skipped.push({ uri: record.uri, reason: `lives in repo ${parts.did}, not ${did}` });
      continue;
    }
    if (seen.has(parts.rkey)) continue; // ignore a duplicate listing of the same record

    let value: Record<string, unknown>;
    try {
      value = asRecordValue(record.value, record.uri);
      // Shape gate: both are required by §4.2 and by every read path. `tile` is also the reason a
      // deployment can be migrated at all without touching a manifest — it points into the unmoved
      // `ing.dasl.masl`, so it is copied through by the spread below, byte for byte.
      readStrongRef(value, "tile", LEGACY_COLLECTION.deployment);
      readLink(value, "root", LEGACY_COLLECTION.deployment);
    } catch (err) {
      skipped.push({ uri: record.uri, reason: reasonOf(err) });
      continue;
    }

    const existingBackRef = backReferencedSite(value);
    if (existingBackRef !== undefined && existingBackRef !== siteRkey) {
      skipped.push({
        uri: record.uri,
        reason: `site back-reference names ${existingBackRef}, not ${siteRkey}`,
      });
      continue;
    }

    // `$type` is the only value that changes; every other field — including `tile`, `root`, `entry`,
    // `createdAt`, and anything this code does not know about — rides through the spread untouched.
    const migratedValue = { ...value, $type: COLLECTION.deployment } as DeploymentRecord;
    const backfilledSite = existingBackRef === undefined;
    if (backfilledSite) migratedValue.site = siteRkey;

    seen.add(parts.rkey);
    deployments.push({
      legacyUri: record.uri,
      uri: atUri(did, COLLECTION.deployment, parts.rkey),
      rkey: parts.rkey,
      cid: await recordCid(migratedValue),
      value: migratedValue,
      backfilledSite,
    });
  }

  // TID rkeys sort chronologically, so this is oldest-first and independent of listing page order.
  deployments.sort((a, b) => (a.rkey < b.rkey ? -1 : a.rkey > b.rkey ? 1 : 0));

  const migratedCurrent = deployments.find((d) => d.rkey === currentRkey);
  if (migratedCurrent === undefined) {
    const why = skipped.find((s) => s.uri === legacyCurrent.uri);
    throw new MigrationShapeError(
      legacySite.uri,
      `its active deployment ${legacyCurrent.uri} is ` +
        (why === undefined ? "not among the supplied legacy deployments" : `unmigratable: ${why.reason}`) +
        " — refusing to plan a migration that would leave `current` dangling",
    );
  }

  const current: StrongRef = { uri: migratedCurrent.uri, cid: migratedCurrent.cid };
  const siteValue = { ...legacySiteValue, $type: COLLECTION.site, current } as SiteRecord;
  const siteCid = await recordCid(siteValue);

  const writes: ApplyWrite[] = deployments.map((d) => ({
    action: "create",
    collection: COLLECTION.deployment,
    rkey: d.rkey,
    value: d.value,
  }));
  writes.push({ action: "create", collection: COLLECTION.site, rkey: siteRkey, value: siteValue });

  return {
    did,
    siteRkey,
    legacySiteUri: legacySite.uri,
    siteUri: atUri(did, COLLECTION.site, siteRkey),
    siteValue,
    siteCid,
    current,
    deployments,
    writes,
    skipped,
  };
}

// ---------------------------------------------------------------------------------------------
// Executor (create pass)
// ---------------------------------------------------------------------------------------------

/** Options for {@link migrate}. */
export interface MigrateInput {
  client: PdsClient;
  /** A plan from {@link planMigration}. */
  plan: MigrationPlan;
  /** Max `swapCommit` retries on concurrent-write conflict; defaults to 3. */
  maxRetries?: number;
}

/** Result of a successful {@link migrate}. */
export interface MigrateResult {
  /** AT-URI of the created `app.szzt.site` record. */
  siteUri: string;
  /** The strong ref `app.szzt.site.current` ended up holding (see {@link MigrateResult.repinned}). */
  current: StrongRef;
  /** AT-URIs of the created `app.szzt.deployment` records, oldest rkey first. */
  deploymentUris: string[];
  /**
   * True when the PDS stored the migrated active deployment under a CID other than the precomputed
   * one and `current` had to be re-pinned by a follow-up guarded write. False on a conforming PDS.
   */
  repinned: boolean;
  /** AT-URIs of the legacy records still in the repo — this pass deletes nothing. */
  legacyRetained: string[];
  /** New repo head after the create commit, when the PDS returns it. */
  commit?: CommitRef;
}

/**
 * Execute a {@link MigrationPlan}: create the whole migrated history plus the site record in ONE
 * atomic, `swapCommit`-guarded `applyWrites` (§7.1 write discipline reused). Purely additive — every
 * write is a `create`, and every legacy record is still there afterward, reported in
 * {@link MigrateResult.legacyRetained}. Removing them is {@link deleteLegacyRecords}, which this
 * function never calls and no option here can reach.
 *
 * Before the write it re-reads the target `app.szzt.site` rkey and refuses
 * ({@link AlreadyMigratedError}, ZERO writes) if a record is already there: re-running a finished
 * migration would overwrite a live site record and re-pin `current` at whatever the plan computed,
 * silently reverting any publish or rollback that happened since. The `swapCommit` guard closes the
 * race between that read and the write.
 *
 * After the write it reads the migrated active deployment back and compares the stored CID against
 * the precomputed pin in `current`. A PDS that re-encoded the record would leave `current` dangling
 * — a permanent fail-closed 502 at every gateway (§9.2) — so on divergence it re-pins via
 * `rollback()`, which re-checks that the deployment and its manifest are intact and guards the
 * pointer write with both `swapRecord` and `swapCommit`.
 *
 * That re-pin branch has never executed. It is covered only by fakes: across a real migration of 2
 * sites and 10 deployments onto an eurosky.social PDS, every stored CID matched its client-side
 * recompute exactly, so the divergence was never observed. The same precompute-and-pin mechanism has
 * been carrying `publish` in production all along, which is the stronger argument that a conforming
 * PDS never diverges — a PDS that re-encoded records would already have broken publishing. The branch
 * stays because the cost of being wrong is a permanently dangling pointer, not because divergence is
 * expected; anyone changing it should know it has no live coverage behind it.
 */
export async function migrate(input: MigrateInput): Promise<MigrateResult> {
  const { client, plan, maxRetries = 3 } = input;

  if (plan.did !== client.did) {
    throw new Error(
      `plan targets repo ${plan.did} but the client is authenticated as ${client.did} — refusing to ` +
        `write another repo's records`,
    );
  }

  let commit: CommitRef | undefined;
  for (let attempt = 0; ; attempt++) {
    const head = await client.getLatestCommit();

    const existing = await client.getRecord(COLLECTION.site, plan.siteRkey);
    if (existing !== null) throw new AlreadyMigratedError(plan.siteUri);

    try {
      const res = await client.applyWrites({ writes: plan.writes, swapCommit: head.cid });
      commit = res.commit;
      break;
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }

  const currentRkey = parseAtUri(plan.current.uri).rkey;
  const stored = await client.getRecord(COLLECTION.deployment, currentRkey);
  if (stored === null) {
    throw new Error(
      `migrated deployment ${plan.current.uri} is absent immediately after its own create write`,
    );
  }
  let current = plan.current;
  let repinned = false;
  if (stored.cid !== plan.current.cid) {
    current = { uri: plan.current.uri, cid: stored.cid };
    await rollback({ client, siteRkey: plan.siteRkey, target: current, maxRetries });
    repinned = true;
  }

  return {
    siteUri: plan.siteUri,
    current,
    deploymentUris: plan.deployments.map((d) => d.uri),
    repinned,
    legacyRetained: [plan.legacySiteUri, ...plan.deployments.map((d) => d.legacyUri)],
    ...(commit !== undefined ? { commit } : {}),
  };
}

// ---------------------------------------------------------------------------------------------
// Deletion (opt-in second pass)
// ---------------------------------------------------------------------------------------------

/** Input to the pure {@link planLegacyDeletion}. */
export interface LegacyDeletionPlanInput {
  /** The legacy `pub.mini.site` record to delete. */
  site: FetchedRecord;
  /** The legacy `pub.mini.deployment` records to delete. */
  deployments: readonly FetchedRecord[];
}

/** One legacy record queued for deletion, paired with the migrated record that must already exist. */
export interface LegacyDeletion {
  /** AT-URI of the legacy record to delete. */
  legacyUri: string;
  /** AT-URI of the migrated counterpart, re-read and required present before any delete lands. */
  migratedUri: string;
  collection: string;
  rkey: string;
}

/** The computed deletion plan: what to delete, what must exist first, and what was refused. */
export interface LegacyDeletionPlan {
  /** Deletions in write order: every deployment, then the site record. */
  deletions: LegacyDeletion[];
  /** The delete writes for one atomic `applyWrites`. Contains ONLY retired-collection rkeys. */
  writes: ApplyWrite[];
  /** Records refused because their URI names no retired collection, each with a reason. */
  refused: SkippedRecord[];
}

/**
 * Plan removal of the legacy originals AFTER a migration has landed (§16 write discipline reused).
 * PURE: writes nothing, and reachable only by calling it — {@link migrate} never does.
 *
 * Deliberately narrow: it emits deletes ONLY for `pub.mini.site`/`pub.mini.deployment` rkeys, and
 * anything else handed to it is refused rather than deleted. In particular no `ing.dasl.masl`
 * manifest record is ever a delete target here — the manifests never moved, and the migrated
 * deployments' `tile` refs still point at them. Reclaiming a manifest (and its blobs) is `prune`'s
 * job, which deletes a deployment and its manifest together once the version is retired.
 */
export function planLegacyDeletion(input: LegacyDeletionPlanInput): LegacyDeletionPlan {
  const deletions: LegacyDeletion[] = [];
  const refused: SkippedRecord[] = [];
  const seen = new Set<string>();

  const consider = (record: FetchedRecord, legacy: string, migrated: string): void => {
    let parts;
    try {
      parts = parseAtUri(record.uri);
    } catch (err) {
      refused.push({ uri: record.uri, reason: reasonOf(err) });
      return;
    }
    if (parts.collection !== legacy) {
      refused.push({
        uri: record.uri,
        reason: `not a ${legacy} record (collection is ${parts.collection}) — this pass only ` +
          `deletes retired ${LEGACY_COLLECTION.site}/${LEGACY_COLLECTION.deployment} records`,
      });
      return;
    }
    const key = `${legacy}/${parts.rkey}`;
    if (seen.has(key)) return;
    seen.add(key);
    deletions.push({
      legacyUri: record.uri,
      migratedUri: atUri(parts.did, migrated, parts.rkey),
      collection: legacy,
      rkey: parts.rkey,
    });
  };

  for (const dep of input.deployments) {
    consider(dep, LEGACY_COLLECTION.deployment, COLLECTION.deployment);
  }
  consider(input.site, LEGACY_COLLECTION.site, COLLECTION.site);

  return {
    deletions,
    writes: deletions.map((d) => ({ action: "delete", collection: d.collection, rkey: d.rkey })),
    refused,
  };
}

/** Options for {@link deleteLegacyRecords}. */
export interface DeleteLegacyInput {
  client: PdsClient;
  /** A plan from {@link planLegacyDeletion}. */
  plan: LegacyDeletionPlan;
  /** Max `swapCommit` retries on concurrent-write conflict; defaults to 3. */
  maxRetries?: number;
}

/** Result of a successful {@link deleteLegacyRecords}. */
export interface DeleteLegacyResult {
  /** AT-URIs of the deleted legacy records. */
  deleted: string[];
  /** New repo head after the delete commit, when the PDS returns it. */
  commit?: CommitRef;
}

/**
 * Execute a {@link LegacyDeletionPlan}: delete the legacy originals in ONE atomic,
 * `swapCommit`-guarded `applyWrites`. This is the explicitly opt-in second pass — nothing in
 * {@link migrate} calls it and no flag on {@link MigrateInput} reaches it.
 *
 * Before the write it re-reads EVERY migrated counterpart and refuses
 * ({@link UnmigratedLegacyRecordError}, ZERO writes) unless all of them exist. That check is the
 * whole safety argument: it is the difference between removing a superseded duplicate and destroying
 * the only copy of a site's history. Re-read at execute time rather than trusted from the migration
 * result, because the two passes are meant to be run separately, possibly much later.
 */
export async function deleteLegacyRecords(input: DeleteLegacyInput): Promise<DeleteLegacyResult> {
  const { client, plan, maxRetries = 3 } = input;
  if (plan.writes.length === 0) return { deleted: [] };

  for (let attempt = 0; ; attempt++) {
    const head = await client.getLatestCommit();

    for (const deletion of plan.deletions) {
      const migrated = parseAtUri(deletion.migratedUri);
      const present = await client.getRecord(migrated.collection, migrated.rkey);
      if (present === null) {
        throw new UnmigratedLegacyRecordError(deletion.legacyUri, deletion.migratedUri);
      }
    }

    try {
      const res = await client.applyWrites({ writes: plan.writes, swapCommit: head.cid });
      return {
        deleted: plan.deletions.map((d) => d.legacyUri),
        ...(res.commit !== undefined ? { commit: res.commit } : {}),
      };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }
}
